import { toGregorian } from './dateUtils';

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

export const resolveTagObjects = (ids, { apiTags = [], prevTags = [], catalogTags = [] } = {}) => {
  const list = Array.isArray(ids) ? ids : [];
  return list
    .map((tagId) => {
      const id = typeof tagId === 'object' ? tagId.id : tagId;
      const fromApi = (apiTags || []).find((t) => sameId(t.id ?? t, id));
      if (fromApi?.name) {
        return { id: fromApi.id ?? id, name: fromApi.name, color: fromApi.color };
      }
      const fromPrev = (prevTags || []).find((t) => sameId(t.id, id));
      if (fromPrev?.name) return fromPrev;
      const fromCat = (catalogTags || []).find((t) => sameId(t.id, id));
      if (fromCat) {
        return {
          id: fromCat.id,
          name: fromCat.name || fromCat.tag_name,
          color: fromCat.color,
        };
      }
      return null;
    })
    .filter(Boolean);
};

export const resolveProductObjects = (ids, { apiProducts = [], prevProducts = [], catalogProducts = [] } = {}) => {
  const list = Array.isArray(ids) ? ids : [];
  return list
    .map((pId) => {
      const id = typeof pId === 'object' ? pId.id : pId;
      const fromApi = (apiProducts || []).find((p) => sameId(p.id ?? p, id));
      if (fromApi?.name) return { id: fromApi.id ?? id, name: fromApi.name };
      const fromPrev = (prevProducts || []).find((p) => sameId(p.id, id));
      if (fromPrev?.name) return fromPrev;
      const fromCat = (catalogProducts || []).find((p) => sameId(p.id, id));
      if (fromCat) return { id: fromCat.id, name: fromCat.name };
      return null;
    })
    .filter(Boolean);
};

/** به‌روزرسانی state سرنخ پس از ویرایش سریع — بدون بازنویسی کامل از API */
export const applyLeadFieldUpdate = (prev, field, value, apiResponse, filters = {}) => {
  if (!prev) return prev;
  const next = { ...prev };
  const api = apiResponse && typeof apiResponse === 'object' ? apiResponse : {};

  switch (field) {
    case 'tags':
      next.tags = resolveTagObjects(value, {
        apiTags: api.tags,
        prevTags: prev.tags,
        catalogTags: filters.tags,
      });
      break;
    case 'category_id': {
      const categories = filters.categories || filters.lead_categories || [];
      const cat = categories.find((c) => sameId(c.id, value));
      next.category_id = value;
      next.category = cat
        ? { id: cat.id, category_name: cat.category_name || cat.name, name: cat.name || cat.category_name }
        : api.category ?? prev.category;
      break;
    }
    case 'source_id': {
      const sources = filters.sources || [];
      const src = sources.find((s) => sameId(s.id, value));
      next.source_id = value;
      next.source = src
        ? { id: src.id, type: src.type || src.name, name: src.type || src.name }
        : api.source ?? prev.source;
      break;
    }
    case 'added_by': {
      const employees = filters.colleagues || filters.employees || [];
      const emp = employees.find((e) => sameId(e.id, value));
      if (api.added_by && typeof api.added_by === 'object') {
        next.added_by = api.added_by;
      } else if (emp) {
        next.added_by = { id: emp.id, name: emp.name };
      } else {
        next.added_by = value;
      }
      break;
    }
    case 'lead_owner':
    case 'owner_id': {
      const employees = filters.colleagues || filters.employees || [];
      const emp = employees.find((e) => sameId(e.id, value));
      const ownerObj =
        (api.lead_owner && typeof api.lead_owner === 'object' && api.lead_owner) ||
        (api.owner && typeof api.owner === 'object' && api.owner) ||
        (emp ? { id: emp.id, name: emp.name } : null);
      next.owner_id = value;
      if (ownerObj) {
        next.lead_owner = ownerObj;
        next.owner = ownerObj;
      } else if (value == null || value === '') {
        next.lead_owner = null;
        next.owner = null;
      }
      break;
    }
    case 'priority':
      next.priority = api.priority ?? value;
      break;
    default:
      if (api[field] !== undefined) next[field] = api[field];
      else next[field] = value;
  }

  return next;
};

/** به‌روزرسانی state معامله پس از ویرایش سریع */
export const applyDealFieldUpdate = (prev, field, value, apiResponse, filters = {}) => {
  if (!prev) return prev;
  const next = { ...prev };
  const api = apiResponse && typeof apiResponse === 'object' ? apiResponse : {};
  const employees = filters.dealWatcher || filters.employees || [];

  switch (field) {
    case 'tags':
      next.tags = resolveTagObjects(value, {
        apiTags: api.tags,
        prevTags: prev.tags,
        catalogTags: filters.tags,
      });
      break;
    case 'product_id': {
      const products = resolveProductObjects(value, {
        apiProducts: api.products,
        prevProducts: prev.products,
        catalogProducts: filters.products,
      });
      next.products = products;
      break;
    }
    case 'category_id': {
      const cat = (filters.categories || []).find((c) => sameId(c.id, value));
      next.category_id = value;
      next.category = cat
        ? { id: cat.id, category_name: cat.category_name || cat.name, name: cat.name || cat.category_name }
        : api.category ?? prev.category;
      break;
    }
    case 'source_id': {
      const sources = filters.sources || [];
      const src = sources.find((s) => sameId(s.id, value));
      next.source_id = value;
      next.source = src
        ? { id: src.id, type: src.type || src.name, name: src.type || src.name }
        : api.source ?? prev.source;
      break;
    }
    case 'deal_owner': {
      const emp = employees.find((e) => sameId(e.id, value));
      if (api.deal_owner && typeof api.deal_owner === 'object') {
        next.deal_owner = api.deal_owner;
      } else if (emp) {
        next.deal_owner = { id: emp.id, name: emp.name };
      } else {
        next.deal_owner = value;
      }
      break;
    }
    case 'priority':
      next.priority = api.priority ?? value;
      break;
    case 'close_date': {
      let nextDate = api.close_date ?? apiResponse?.close_date ?? value;
      if (typeof nextDate === 'string' && /^\d{4}\/\d{2}\/\d{2}/.test(nextDate.trim())) {
        nextDate = toGregorian(nextDate.trim()) || nextDate;
      }
      next.close_date = nextDate;
      break;
    }
    default:
      if (api[field] !== undefined) next[field] = api[field];
      else next[field] = value;
  }

  return next;
};
